import React, { useState, useEffect, useCallback } from 'react';
import { MdArrowForward, MdPerson } from 'react-icons/md';
import { useAuthStore } from '../store/authStore';
import {
  getMyCustomerProfile,
  updateMyCustomerProfile,
  Customer,
} from '../services/customersApi';
import {
  listMyAddresses,
  createMyAddress,
  updateMyAddress,
  deleteMyAddress,
  Address as BackendAddress,
} from '../services/addressesApi';
import { apiClient } from '../services/apiClient';
import '../styles/ProfilePage.css';

type Tab = 'account' | 'orders' | 'address' | 'password';

const tabs: { key: Tab; label: string }[] = [
  { key: 'account', label: 'Account info' },
  { key: 'orders', label: 'My order' },
  { key: 'address', label: 'My address' },
  { key: 'password', label: 'Change password' },
];

const ProfilePage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const [activeTab, setActiveTab] = useState<Tab>('account');

  const [profile, setProfile] = useState<Customer | null>(null);
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Address book state — populated when the user opens the "My address" tab.
  const [addresses, setAddresses] = useState<BackendAddress[]>([]);
  const [addrError, setAddrError] = useState<string | null>(null);
  const [showAddrForm, setShowAddrForm] = useState(false);
  const [editingAddrId, setEditingAddrId] = useState<number | null>(null);
  const [addrForm, setAddrForm] = useState({
    address_line1: '', address_line2: '', city: '', state: '', zip_code: '', is_default: false,
  });

  // Change-password form state.
  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNext, setPwdNext] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdDone, setPwdDone] = useState(false);

  const refreshAddresses = useCallback(async () => {
    setAddrError(null);
    try {
      const rows = await listMyAddresses();
      setAddresses(rows);
    } catch (err) {
      setAddrError(err instanceof Error ? err.message : 'Failed to load addresses');
    }
  }, []);

  // Lazy-load addresses the first time the user opens the address tab.
  useEffect(() => {
    if (activeTab === 'address') refreshAddresses();
  }, [activeTab, refreshAddresses]);

  const startAddAddress = () => {
    setEditingAddrId(null);
    setAddrForm({ address_line1: '', address_line2: '', city: '', state: '', zip_code: '', is_default: addresses.length === 0 });
    setShowAddrForm(true);
  };

  const startEditAddress = (a: BackendAddress) => {
    setEditingAddrId(a.id);
    setAddrForm({
      address_line1: a.address_line1,
      address_line2: a.address_line2 ?? '',
      city: a.city,
      state: a.state,
      zip_code: a.zip_code,
      is_default: a.is_default,
    });
    setShowAddrForm(true);
  };

  const handleAddrSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddrError(null);
    if (!addrForm.address_line1.trim() || !addrForm.city.trim() || addrForm.state.length !== 2 || !addrForm.zip_code.trim()) {
      setAddrError('Street, city, 2-letter state, and ZIP are required');
      return;
    }
    try {
      if (editingAddrId == null) {
        await createMyAddress({
          address_type: 'shipping',
          address_line1: addrForm.address_line1.trim(),
          address_line2: addrForm.address_line2.trim() || undefined,
          city: addrForm.city.trim(),
          state: addrForm.state.toUpperCase(),
          zip_code: addrForm.zip_code.trim(),
          is_default: addrForm.is_default,
        });
      } else {
        await updateMyAddress(editingAddrId, {
          address_line1: addrForm.address_line1.trim(),
          address_line2: addrForm.address_line2.trim() || undefined,
          city: addrForm.city.trim(),
          state: addrForm.state.toUpperCase(),
          zip_code: addrForm.zip_code.trim(),
          is_default: addrForm.is_default,
        });
      }
      setShowAddrForm(false);
      setEditingAddrId(null);
      await refreshAddresses();
    } catch (err) {
      setAddrError(err instanceof Error ? err.message : 'Failed to save address');
    }
  };

  const handleAddrDelete = async (id: number) => {
    setAddrError(null);
    try {
      await deleteMyAddress(id);
      await refreshAddresses();
    } catch (err) {
      setAddrError(err instanceof Error ? err.message : 'Failed to delete address');
    }
  };

  const handleAddrMakeDefault = async (id: number) => {
    setAddrError(null);
    try {
      await updateMyAddress(id, { is_default: true });
      await refreshAddresses();
    } catch (err) {
      setAddrError(err instanceof Error ? err.message : 'Failed to set default');
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError(null);
    if (!pwdCurrent || !pwdNext || pwdNext !== pwdConfirm) {
      setPwdError('Fill all fields and confirm the new password');
      return;
    }
    if (pwdNext.length < 12) {
      setPwdError('New password must be at least 12 characters');
      return;
    }
    setPwdBusy(true);
    try {
      await apiClient.post<void>('/users/me/password', { currentPassword: pwdCurrent, newPassword: pwdNext });
      setPwdDone(true);
      setPwdCurrent(''); setPwdNext(''); setPwdConfirm('');
      setTimeout(() => setPwdDone(false), 2000);
    } catch (err) {
      setPwdError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setPwdBusy(false);
    }
  };

  // Hydrate the editable form from the canonical /customers/me record so
  // edits round-trip cleanly through PATCH /customers/:id.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await getMyCustomerProfile();
        if (cancelled) return;
        setProfile(c);
        const [first, ...rest] = (c.full_name || '').trim().split(/\s+/);
        setFirstName(first || '');
        setLastName(rest.join(' ') || '');
        setEmail(c.email);
        setPhone(c.contact_phone ?? '');
      } catch {
        // Fallback to whatever the auth store already has.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) {
      // No backend profile loaded – just flash "saved" to keep UX consistent.
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      // Email is read-only on the backend (no field in the customer-self
      // whitelist), so we only send the fields the API will accept.
      const updated = await updateMyCustomerProfile(profile.id, {
        full_name: `${firstName} ${lastName}`.trim(),
        contact_phone: phone || undefined,
      });
      setProfile(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile">
      <div className="profile__container">
        {/* ── Left Sidebar ── */}
        <aside className="profile__sidebar">
          <div className="profile__avatar">
            <MdPerson />
          </div>
          <h2 className="profile__name">{user?.firstName} {user?.lastName}</h2>
          <p className="profile__email">{user?.email}</p>

          <nav className="profile__nav">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`profile__nav-item ${activeTab === tab.key ? 'profile__nav-item--active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                <MdArrowForward />
              </button>
            ))}
          </nav>
        </aside>

        {/* ── Right Content ── */}
        <main className="profile__content">
          {activeTab === 'account' && (
            <>
              <h1 className="profile__title">Account Info</h1>

              {profile && profile.credit_limit != null && (
                <div className="profile__credit-summary">
                  <div className="profile__credit-stat">
                    <span className="profile__credit-label">Credit Limit</span>
                    <span className="profile__credit-value">
                      ${profile.credit_limit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="profile__credit-stat">
                    <span className="profile__credit-label">Outstanding Balance</span>
                    <span className="profile__credit-value profile__credit-value--balance">
                      ${profile.metrics.outstanding_balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="profile__credit-stat">
                    <span className="profile__credit-label">Available Credit</span>
                    <span className="profile__credit-value profile__credit-value--available">
                      ${Math.max(0, profile.credit_limit - profile.metrics.outstanding_balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}

              <form className="profile__form" onSubmit={handleSave}>
                <div className="profile__form-row">
                  <div className="profile__field">
                    <label className="profile__label">First Name <span className="profile__required">*</span></label>
                    <input className="profile__input" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  </div>
                  <div className="profile__field">
                    <label className="profile__label">Last Name <span className="profile__required">*</span></label>
                    <input className="profile__input" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  </div>
                </div>
                <div className="profile__field">
                  <label className="profile__label">Email Address <span className="profile__required">*</span></label>
                  <input className="profile__input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="profile__field">
                  <label className="profile__label">Phone Number <span className="profile__optional">(Optional)</span></label>
                  <input className="profile__input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 0231 4554 452" />
                </div>
                {error && (
                  <p style={{ color: '#EF4444', marginTop: 8, fontSize: '0.85rem' }}>{error}</p>
                )}
                <button type="submit" className="profile__save" disabled={saving}>
                  {saving ? 'SAVING...' : saved ? 'SAVED!' : 'SAVE'}
                </button>
              </form>
            </>
          )}

          {activeTab === 'orders' && (
            <>
              <h1 className="profile__title">My Orders</h1>
              <div className="profile__empty">
                <p>You have no orders yet.</p>
              </div>
            </>
          )}

          {activeTab === 'address' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1 className="profile__title">My Addresses</h1>
                {!showAddrForm && (
                  <button type="button" className="profile__save" onClick={startAddAddress}>
                    + Add Address
                  </button>
                )}
              </div>

              {addrError && (
                <p style={{ color: '#EF4444', background: '#FEF2F2', padding: 8, borderRadius: 6, fontSize: 13 }}>
                  {addrError}
                </p>
              )}

              {showAddrForm && (
                <form className="profile__form" onSubmit={handleAddrSave} style={{ marginBottom: 16, padding: 16, background: '#F9FAFB', borderRadius: 8 }}>
                  <div className="profile__field">
                    <label className="profile__label">Street <span className="profile__required">*</span></label>
                    <input className="profile__input" value={addrForm.address_line1}
                           onChange={(e) => setAddrForm({ ...addrForm, address_line1: e.target.value })} required />
                  </div>
                  <div className="profile__field">
                    <label className="profile__label">Apt / Suite (optional)</label>
                    <input className="profile__input" value={addrForm.address_line2}
                           onChange={(e) => setAddrForm({ ...addrForm, address_line2: e.target.value })} />
                  </div>
                  <div className="profile__form-row">
                    <div className="profile__field">
                      <label className="profile__label">City <span className="profile__required">*</span></label>
                      <input className="profile__input" value={addrForm.city}
                             onChange={(e) => setAddrForm({ ...addrForm, city: e.target.value })} required />
                    </div>
                    <div className="profile__field">
                      <label className="profile__label">State (2-letter) <span className="profile__required">*</span></label>
                      <input className="profile__input" value={addrForm.state} maxLength={2}
                             onChange={(e) => setAddrForm({ ...addrForm, state: e.target.value.toUpperCase() })} required />
                    </div>
                    <div className="profile__field">
                      <label className="profile__label">ZIP <span className="profile__required">*</span></label>
                      <input className="profile__input" value={addrForm.zip_code}
                             onChange={(e) => setAddrForm({ ...addrForm, zip_code: e.target.value })} required />
                    </div>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 14, color: '#374151' }}>
                    <input type="checkbox" checked={addrForm.is_default}
                           onChange={(e) => setAddrForm({ ...addrForm, is_default: e.target.checked })} />
                    Set as default shipping address
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="submit" className="profile__save">
                      {editingAddrId == null ? 'Save Address' : 'Update Address'}
                    </button>
                    <button type="button" className="profile__save" style={{ background: '#fff', border: '1px solid #D1D5DB', color: '#374151' }}
                            onClick={() => { setShowAddrForm(false); setEditingAddrId(null); }}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {addresses.length === 0 && !showAddrForm && (
                <div className="profile__address-card">
                  <p>No saved addresses. Add one to speed up checkout.</p>
                </div>
              )}

              {addresses.map((a) => (
                <div className="profile__address-card" key={a.id} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <p>
                        <strong>{a.address_line1}</strong>
                        {a.is_default && (
                          <span style={{ marginLeft: 8, color: '#10B981', fontSize: 12, fontWeight: 600 }}>DEFAULT</span>
                        )}
                      </p>
                      {a.address_line2 && <p>{a.address_line2}</p>}
                      <p>{a.city}, {a.state} {a.zip_code}</p>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {!a.is_default && (
                        <button type="button" onClick={() => handleAddrMakeDefault(a.id)}
                                style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer' }}>
                          Make Default
                        </button>
                      )}
                      <button type="button" onClick={() => startEditAddress(a)}
                              style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer' }}>
                        Edit
                      </button>
                      <button type="button" onClick={() => handleAddrDelete(a.id)}
                              style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid #FCA5A5', background: '#FEF2F2', color: '#B91C1C', cursor: 'pointer' }}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {activeTab === 'password' && (
            <>
              <h1 className="profile__title">Change Password</h1>
              <form className="profile__form" onSubmit={handleChangePassword}>
                {pwdError && (
                  <p style={{ color: '#EF4444', background: '#FEF2F2', padding: 8, borderRadius: 6, fontSize: 13 }}>
                    {pwdError}
                  </p>
                )}
                <div className="profile__field">
                  <label className="profile__label">Current Password <span className="profile__required">*</span></label>
                  <input className="profile__input" type="password" placeholder="Enter current password"
                         value={pwdCurrent} onChange={(e) => setPwdCurrent(e.target.value)} required />
                </div>
                <div className="profile__field">
                  <label className="profile__label">New Password (min 12 chars) <span className="profile__required">*</span></label>
                  <input className="profile__input" type="password" placeholder="Enter new password"
                         value={pwdNext} onChange={(e) => setPwdNext(e.target.value)} required minLength={12} />
                </div>
                <div className="profile__field">
                  <label className="profile__label">Confirm New Password <span className="profile__required">*</span></label>
                  <input className="profile__input" type="password" placeholder="Confirm new password"
                         value={pwdConfirm} onChange={(e) => setPwdConfirm(e.target.value)} required />
                </div>
                <button type="submit" className="profile__save" disabled={pwdBusy}>
                  {pwdBusy ? 'UPDATING...' : pwdDone ? 'SAVED!' : 'SAVE'}
                </button>
              </form>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default ProfilePage;
